import { card, person } from "@universityofmaryland/web-elements-library/composite";
import { createTextWithLink, createTextContainer, createImageOrLinkedImage } from "@universityofmaryland/web-utilities-library/elements";
const CONTACT_CONFIGS = [
  {
    key: "email",
    label: () => "Email",
    url: (value) => `mailto:${value}`
  },
  {
    key: "website",
    label: (value) => value,
    url: (value) => value
  },
  {
    key: "linkedin",
    label: (value) => value,
    url: (value) => value
  },
  {
    key: "twitter",
    label: (value) => value,
    url: (value) => value
  }
];
const buildFullName = (entry) => {
  const parts = [
    entry.prefix,
    entry.firstName,
    entry.middleName,
    entry.lastName,
    entry.suffix
  ].filter(Boolean);
  return parts.join(" ");
};
const buildProfileUrl = (entry) => {
  return `https://umdrightnow.umd.edu/expert/${entry.slug}`;
};
const extractPrimaryJobTitle = (entry) => {
  return entry.organizations?.[0]?.jobs?.[0]?.title || null;
};
const extractPrimaryAssociation = (entry) => {
  const campusUnit = entry.organizations?.[0]?.jobs?.[0]?.campusUnits?.[0];
  if (!campusUnit) return null;
  return {
    title: campusUnit.title,
    url: campusUnit.link?.url
  };
};
const extractImageData = (entry, fullName) => {
  const headshotUrl = entry.headshot?.[0]?.url;
  if (!headshotUrl) return null;
  return {
    url: headshotUrl,
    altText: fullName
  };
};
const extractContactData = (entry) => {
  return {
    email: entry.email || null,
    website: entry.website || null,
    linkedin: entry.linkedin || null,
    twitter: entry.twitter || null
  };
};
const extractDescription = (entry, displayType) => {
  return entry.summary?.html || null;
};
const extractPronouns = (entry) => {
  return entry.pronouns || null;
};
const extractAreasOfExpertise = (entry) => {
  const areas = entry.areasOfExpertise;
  if (!areas || areas.length === 0) {
    return null;
  }
  return areas.map((area) => area.title).join(", ");
};
const createNameElement = (fullName, url, containerTag) => {
  return createTextWithLink({
    text: fullName,
    url,
    containerTag
  });
};
const createJobElement = (jobTitle) => {
  if (!jobTitle) return null;
  return createTextContainer({ text: jobTitle });
};
const createAssociationElement = (association) => {
  if (!association) return null;
  if (association.url) {
    return createTextWithLink({
      text: association.title,
      url: association.url
    });
  }
  return createTextContainer({ text: association.title });
};
const createImageElement = (imageData, linkUrl, linkLabel) => {
  if (!imageData) return null;
  return createImageOrLinkedImage({
    imageUrl: imageData.url,
    altText: imageData.altText,
    linkUrl,
    linkLabel
  });
};
const createDescriptionElement = (description) => {
  if (!description) return null;
  return createTextContainer({ text: description, allowHTML: true });
};
const createPronounsElement = (pronouns) => {
  if (!pronouns) return null;
  return createTextContainer({ text: pronouns });
};
const createExpertiseElement = (expertise) => {
  if (!expertise) return null;
  return createTextContainer({ text: expertise });
};
const createContactElements = (contactData) => {
  return CONTACT_CONFIGS.reduce(
    (elements, config) => {
      const value = contactData[config.key];
      if (!value) return elements;
      const element = createTextWithLink({
        text: config.label(value),
        url: config.url(value)
      });
      elements[config.key] = element;
      return elements;
    },
    {}
  );
};
const createBlockCardProps = (entry, options) => {
  const { isThemeDark = false } = options;
  const fullName = buildFullName(entry);
  const profileUrl = buildProfileUrl(entry);
  const jobTitle = extractPrimaryJobTitle(entry);
  const association = extractPrimaryAssociation(entry);
  const imageData = extractImageData(entry, fullName);
  const pronouns = extractPronouns(entry);
  const expertise = extractAreasOfExpertise(entry);
  const name = createNameElement(fullName, profileUrl);
  createJobElement(jobTitle);
  const associationElement = createAssociationElement(association);
  const image = createImageElement(
    imageData,
    profileUrl,
    `View profile for ${fullName}`
  );
  const pronounsElement = createPronounsElement(pronouns);
  const expertiseElement = createExpertiseElement(expertise);
  return person.block({
    name,
    slotOne: expertiseElement,
    slotTwo: associationElement,
    slotThreeItalic: pronounsElement,
    image,
    isThemeDark
  });
};
const createListCardProps = (entry, options) => {
  const { isThemeDark = false } = options;
  const fullName = buildFullName(entry);
  const profileUrl = buildProfileUrl(entry);
  const jobTitle = extractPrimaryJobTitle(entry);
  const association = extractPrimaryAssociation(entry);
  const imageData = extractImageData(entry, fullName);
  const pronouns = extractPronouns(entry);
  const expertise = extractAreasOfExpertise(entry);
  const name = createNameElement(fullName, profileUrl);
  createJobElement(jobTitle);
  const associationElement = createAssociationElement(association);
  const image = createImageElement(
    imageData,
    profileUrl,
    `View profile for ${fullName}`
  );
  const pronounsElement = createPronounsElement(pronouns);
  const expertiseElement = createExpertiseElement(expertise);
  return person.list({
    name,
    slotOne: expertiseElement,
    slotThreeItalic: pronounsElement,
    slotTwo: associationElement,
    image,
    isThemeDark
  });
};
const createTabularCardProps = (entry, options) => {
  const { isThemeDark = false } = options;
  const fullName = buildFullName(entry);
  const profileUrl = buildProfileUrl(entry);
  const jobTitle = extractPrimaryJobTitle(entry);
  const association = extractPrimaryAssociation(entry);
  const imageData = extractImageData(entry, fullName);
  const contactData = extractContactData(entry);
  const pronouns = extractPronouns(entry);
  const expertise = extractAreasOfExpertise(entry);
  const name = createNameElement(fullName, profileUrl);
  createJobElement(jobTitle);
  const associationElement = createAssociationElement(association);
  const image = createImageElement(
    imageData,
    profileUrl,
    `View profile for ${fullName}`
  );
  const contactElements = createContactElements(contactData);
  const pronounsElement = createPronounsElement(pronouns);
  const expertiseElement = createExpertiseElement(expertise);
  return person.tabular({
    name,
    slotOne: expertiseElement,
    slotThreeItalic: pronounsElement,
    slotTwo: associationElement,
    image,
    ...contactElements,
    isThemeDark
  });
};
const createOverlayCardProps = (entry, options) => {
  const { isThemeDark = false } = options;
  const fullName = buildFullName(entry);
  const profileUrl = buildProfileUrl(entry);
  const jobTitle = extractPrimaryJobTitle(entry);
  const imageData = extractImageData(entry, fullName);
  const headline = createNameElement(fullName, profileUrl);
  const text = createJobElement(jobTitle);
  const backgroundImage = createImageElement(
    imageData,
    profileUrl,
    `View profile for ${fullName}`
  );
  return card.overlay.image({
    headline,
    text,
    backgroundImage,
    isThemeDark
  });
};
const mapExpertToBioProps = (entry, displayType, isThemeDark = false) => {
  const fullName = buildFullName(entry);
  const profileUrl = buildProfileUrl(entry);
  const jobTitle = extractPrimaryJobTitle(entry);
  const association = extractPrimaryAssociation(entry);
  const imageData = extractImageData(entry, fullName);
  const contactData = extractContactData(entry);
  const description = extractDescription(entry);
  const pronouns = extractPronouns(entry);
  const expertise = extractAreasOfExpertise(entry);
  const name = createNameElement(fullName, profileUrl, "h1");
  createJobElement(jobTitle);
  const associationElement = createAssociationElement(association);
  const image = createImageElement(imageData);
  const descriptionElement = createDescriptionElement(description);
  const contactElements = createContactElements(contactData);
  const pronounsElement = createPronounsElement(pronouns);
  const expertiseElement = createExpertiseElement(expertise);
  return {
    name,
    slotTwoItalic: pronounsElement,
    slotTwo: associationElement,
    slotOne: expertiseElement,
    email: contactElements.email || null,
    linkedin: contactElements.linkedin || null,
    phone: null,
    address: null,
    additionalContact: null,
    image,
    description: descriptionElement,
    isThemeDark
  };
};
const expertsDisplayStrategy = {
  layoutType: "list",
  mapEntryToCard: (entry, options) => {
    const { isOverlay = false, cardType = "block" } = options;
    if (isOverlay && entry.headshot?.[0]?.url) {
      return createOverlayCardProps(entry, options);
    }
    switch (cardType) {
      case "list":
        return createListCardProps(entry, options);
      case "tabular":
        return createTabularCardProps(entry, options);
      default:
        return createBlockCardProps(entry, options);
    }
  }
};
export {
  buildFullName,
  expertsDisplayStrategy,
  extractAreasOfExpertise,
  mapExpertToBioProps
};
//# sourceMappingURL=experts.js.map
